import HttpService from '../../../../../common/http/HttpService';
import { ContactEndpoint } from '../../../../../common/endpoints/modules/administration/ContactEndpoint';

const titleOptions = {
  m: { value: 'm', label: 'userProfile.MALE_TITLE' },
  w: { value: 'w', label: 'userProfile.FEMALE_TITLE' },
  v: { value: 'v', label: 'userProfile.GENDER_NEUTRAL_TITLE' },
};

function makeUser() {
  return {
    title: null,
    firstName: null,
    lastName: null,
    email: null,
    userGroup: null,
    legacyUserGroup: null,
    language: null,
    theme: null,
    country: null,
    timezone: null,
    company: null,
    function: null,
    street: null,
    street2: null,
    zipcode: null,
    city: null,
    phone: null,
    cellphone: null,
    fax: null,
    taxNumber: null,
    username: '',
    password: '',
    passwordConfirmation: '',
    systems: [],
    teams: [],
    assignNewSystemsAutomatically: false,
  };
}

function makeContact() {
  return {
    title: null,
    firstName: null,
    lastName: null,
    email: null,
    language: null,
    country: null,
    timezone: null,
    company: null,
    function: null,
    street: null,
    street2: null,
    zipcode: null,
    city: null,
    phone: null,
    cellphone: null,
    fax: null,
    taxNumber: null,
  };
}

function parseContact(contact) {
  return {
    ...contact,
    ...{
      title: contact.title.value,
      language: contact.language.value,
      country: contact.country.value,
      timezone: contact.timezone.value,
    },
  };
}

function parseUser(user) {
  return {
    ...user,
    ...{
      title: user.title?.value ?? null,
      language: user.language.value,
      country: user.country.value,
      timezone: user.timezone.value,
      userGroup: user.userGroup ? user.userGroup.value : null,
      legacyUserGroup: user.legacyUserGroup?.value ?? null,
      theme: user.theme.value,
    },
  };
}

function addUserInformation(contact, baseInfo) {
  const themes = baseInfo.themes.default.concat(baseInfo.themes.userDefined);
  const systems = baseInfo.systems.filter((system) => contact.systems.includes(system.id));
  const systemIds = systems.map((system) => system.id);

  return {
    ...contact,
    ...{
      userGroup: baseInfo.userGroups.find((option) => option.value === contact.userGroup),
      legacyUserGroup: baseInfo.legacyUserGroups
        .find((option) => option.value === contact.legacyUserGroup),
      theme: themes.find((theme) => theme.value === contact.theme),
      systems: systemIds,
      teams: contact.teams,
      password: '',
    },
  };
}

function prepareContact(contact, baseInfo, userProfileSettings) {
  const languages = Object.entries(userProfileSettings.languages)
    .map(([languageCode, language]) => ({ value: languageCode, text: language }));

  return {
    ...contact,
    ...{
      title: titleOptions[contact.title],
      language: languages.find((language) => language.value === contact.language),
      country: baseInfo.countries.find((country) => country.value === contact.country),
      timezone: baseInfo.timezones.find((timezone) => timezone.value === contact.timezone),
    },
  };
}

async function createUser(user) {
  const parsedUser = parseUser(user);

  const response = await HttpService.post(ContactEndpoint.CREATE, { user: parsedUser });
  return response.data;
}

async function editUserBase(user, userId) {
  const parsedUser = parseUser(user);
  const response = await HttpService.post(
    ContactEndpoint.EDIT_BASE,
    { user: parsedUser, userId },
  );
  return response.data;
}

async function editUserAuthentication(user, userId) {
  const parsedUser = parseUser(user);
  const response = await HttpService.post(
    ContactEndpoint.EDIT_AUTHENTICATION,
    { user: parsedUser, userId },
  );
  return response.data;
}

async function editUserTeamsSystems(user, userId) {
  const parsedUser = parseUser(user);
  const response = await HttpService.post(
    ContactEndpoint.EDIT_TEAMS_SYSTEMS,
    { user: parsedUser, userId },
  );
  return response.data;
}

async function createContact(contact) {
  const parsedContact = parseContact(contact);

  const response = await HttpService.post(ContactEndpoint.CREATE, { contact: parsedContact });
  return response.data;
}

async function editContact(contact, userId) {
  const parsedContact = parseContact(contact);
  const response = await HttpService.post(
    ContactEndpoint.EDIT_CONTACT,
    { contact: parsedContact, userId },
  );
  return response.data;
}

async function get(userId) {
  const response = await HttpService.post(ContactEndpoint.GET, { userId });
  return response.data;
}

async function getContact(userId, baseInfo, userProfileSettings) {
  const contact = await get(userId);
  return prepareContact(contact, baseInfo, userProfileSettings);
}

async function getUser(userId, baseInfo, userProfileSettings) {
  const contact = await get(userId);
  const contactData = prepareContact(contact, baseInfo, userProfileSettings);
  return addUserInformation(contactData, baseInfo);
}

async function getBaseInfo() {
  const response = await HttpService.get(ContactEndpoint.GET_BASE_INFO);
  return response.data;
}

async function getSystemsInfo(systemFilters) {
  const response = await HttpService.post(
    ContactEndpoint.GET_SYSTEMS_INFO,
    {
      systemFilters,
    },
  );
  return response.data;
}

async function getTeamsInfo() {
  const response = await HttpService.get(ContactEndpoint.GET_TEAMS_INFO);
  return response.data;
}

async function deleteUser(id) {
  return HttpService.post(ContactEndpoint.DELETE, { id });
}

const ContactService = {
  deleteUser,
  createUser,
  createContact,
  editContact,
  editUserBase,
  editUserAuthentication,
  editUserTeamsSystems,
  get,
  getBaseInfo,
  getContact,
  getSystemsInfo,
  getTeamsInfo,
  getUser,
  makeUser,
  makeContact,
  titleOptions,
};

export default ContactService;
